import {EmptyState, Illustration} from '@sollapay/ui';
import {SearchIllustration} from '@sollapay/ui/illustrations';
import {useTranslation} from 'react-i18next';

import {AssetTrustSummaryCard, AssetTrustSummaryCardSkeleton} from '@/features/buyers/components';
import {usePurchaseSummaryQuery} from '@/features/buyers/hooks';
import {
  useTrustAccountActivityFeedQuery,
  useTrustAccountStatsQuery
} from '@/features/trust-accounts/hooks';
import {useCurrentTrustAccount, useTrustTypeStrategy} from '@/features/trust-accounts/providers';

import {ActivityFeedTable} from './ActivityFeedTable';
import {ActivityKpiCards} from './ActivityKpiCards';
import {ActivityTabContentSkeleton} from './ActivityTabContent.skeleton';
import {ShowMoreButton} from './ShowMoreButton';

import type {TrustAccountActivityFeedDTO} from '@sollapay/types';

export function ActivityTabContent() {
  const {t} = useTranslation('trustAccounts');
  const {id: trustAccountId} = useCurrentTrustAccount();
  const feedQuery = useTrustAccountActivityFeedQuery(trustAccountId);
  const statsQuery = useTrustAccountStatsQuery(trustAccountId);
  const {allowsMultiplePurchases} = useTrustTypeStrategy();

  // General-sale only — feeds AssetTrustSummaryCard without fetching every buyer/payment.
  const {data: purchaseSummary, isLoading: isPurchaseSummaryLoading} = usePurchaseSummaryQuery(
    trustAccountId,
    {enabled: !allowsMultiplePurchases}
  );

  const activities =
    feedQuery.data?.pages.flatMap((page: TrustAccountActivityFeedDTO) => page.activities) ?? [];
  const hasMore = feedQuery.data?.pages.at(-1)?.hasMore ?? false;
  const total = feedQuery.data?.pages[0]?.total ?? 0;

  if (feedQuery.isLoading || statsQuery.isLoading) {
    return <ActivityTabContentSkeleton showsAssetSummary={!allowsMultiplePurchases} />;
  }

  const emptyIllustration = (
    <Illustration width={166} height={160}>
      <SearchIllustration />
    </Illustration>
  );

  const summaryCard = allowsMultiplePurchases ? null : isPurchaseSummaryLoading ? (
    <AssetTrustSummaryCardSkeleton />
  ) : (
    purchaseSummary && (
      <AssetTrustSummaryCard
        purchase={purchaseSummary.purchase}
        amountToPayNis={purchaseSummary.amountToPayNis}
        depositPaidNis={purchaseSummary.depositPaidNis}
        trustAccountId={trustAccountId}
      />
    )
  );

  return (
    <div className="flex flex-col gap-8">
      {summaryCard}
      {statsQuery.data && <ActivityKpiCards stats={statsQuery.data} />}
      {activities.length === 0 ? (
        <EmptyState
          illustration={emptyIllustration}
          title={t('view.activity.empty.title')}
          subtitle={t('view.activity.empty.description')}
        />
      ) : (
        <>
          <ActivityFeedTable
            activities={activities}
            loadedCount={activities.length}
            total={total}
          />
          {hasMore && (
            <ShowMoreButton
              onLoadMore={() => void feedQuery.fetchNextPage()}
              isLoading={feedQuery.isFetchingNextPage}
            />
          )}
        </>
      )}
    </div>
  );
}
